import React, { useState, useEffect } from 'react';
import { FaCalendarAlt, FaUserCheck, FaTimes } from 'react-icons/fa';
import {
  faChartPie,
  faChartBar,
  faUsers,
  faListAlt,
  faEdit,
  faTrash,
  faPlus,
  faSpinner
} from '@fortawesome/free-solid-svg-icons'; // removed faEvent
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FaTimesCircle } from 'react-icons/fa';
import { useToast } from '../contexts/ToastContext';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';

const API_BASE = 'https://dev.sliitleo.org/Backend/events.php';

const EventsPage = () => {
  const { showToast } = useToast();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [formData, setFormData] = useState({
    event_name: '',
    event_date: '',
    start_time: '',
    end_time: '',
    location: '',
    description: ''
  });

  // Format time for input field
  const formatTimeForInput = (timeString) => {
    if (!timeString) return '';
    if (timeString.includes(':')) {
      return timeString.split(':').slice(0, 2).join(':');
    }
    return timeString;
  };

  // Format date for display
  const formatDate = (dateString) => {
    const options = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      weekday: 'short'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Fetch all events
  const fetchEvents = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(API_BASE, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Failed to fetch events');
      const data = await response.json();
      // accept either {success, data} or raw array
      const rows = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
      setEvents(rows);
    } catch (error) {
      console.error('Error fetching events:', error);
      setError('Failed to load events. Please try again.');
      showToast('Failed to load events', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  // Open modal for adding new event
  const openAddModal = () => {
    setCurrentEvent(null);
    setFormData({
      event_name: '',
      event_date: '',
      start_time: '',
      end_time: '',
      location: '',
      description: ''
    });
    setIsModalOpen(true);
  };

  // Open modal for editing event
  const openEditModal = (event) => {
    setCurrentEvent(event);
    setFormData({
      event_name: event.event_name,
      event_date: event.event_date,
      start_time: event.start_time || '',
      end_time: event.end_time || '',
      location: event.location || '',
      description: event.description || ''
    });
    setIsModalOpen(true);
  };

  // Handle form submission (add/edit)
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = currentEvent
        ? `https://dev.sliitleo.org/Backend/events.php?id=${currentEvent.id}` // fixed stray "/"
        : 'https://dev.sliitleo.org/Backend/events.php';

      const method = currentEvent ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok || payload?.success === false) {
        throw new Error(payload?.error || 'Failed to save event');
      }

      await fetchEvents();
      setIsModalOpen(false);
      showToast(`Event ${currentEvent ? 'updated' : 'created'} successfully`, 'success');
    } catch (error) {
      console.error('Error saving event:', error);
      showToast(error.message || 'Failed to save event', 'error');
    }
  };

  /// State for delete confirmation
const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
const [eventToDelete, setEventToDelete] = useState(null);

// Open delete confirmation
const confirmDelete = (eventId) => {
  setEventToDelete(eventId);
  setShowDeleteConfirm(true);
};

// Handle event deletion (POST override to avoid CORS preflight)
const handleDelete = async (shouldDelete) => {
  if (!shouldDelete || !eventToDelete) {
    setShowDeleteConfirm(false);
    setEventToDelete(null);
    return;
  }

  const id = eventToDelete; // capture before we clear it

  try {
    // Use POST + _method=DELETE with a "simple" content-type (no preflight)
    const res = await fetch(`${API_BASE}?id=${encodeURIComponent(id)}&_method=DELETE`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
      body: '' // keep empty to remain a simple request
    });

    // Try to parse JSON if server returns it; otherwise just rely on status
    let payload = null;
    const ct = res.headers.get('content-type') || '';
    if (ct.includes('application/json')) {
      try { payload = await res.json(); } catch {}
    }

    if (!res.ok || (payload && payload.success === false)) {
      throw new Error((payload && payload.error) || `Failed to delete (status ${res.status})`);
    }

    // Success: close modal and optimistically remove from UI
    setShowDeleteConfirm(false);
    setEventToDelete(null);
    setEvents((prev) => prev.filter((ev) => String(ev.id) !== String(id)));
    showToast('Event deleted successfully', 'success');
  } catch (error) {
    console.error('Error deleting event:', error);
    setShowDeleteConfirm(false);
    setEventToDelete(null);
    showToast(error.message || 'Failed to delete event', 'error');
  }
};

// Fetch events on component mount
useEffect(() => {
  fetchEvents();
}, []);

  const user = JSON.parse(localStorage.getItem('user')) || {
    name: 'Unknown User',
    SLIITID: '0',
    role: 'Member',
    image: '/user.png'
  };

  return (
    <div className="flex flex-col md:flex-row bg-[#111827] text-white min-h-screen">
      <Sidebar />
      <Topbar user={user} />

      <main className="md:ml-64 ml-0 mt-14 w-full px-2 sm:px-4 md:px-6 lg:px-8 py-4 md:py-6 lg:py-10">
        {/* ====== KEEPING YOUR ORIGINAL HEADER EXACTLY ====== */}
        <div className="backdrop-blur-md bg-gradient-to-r from-[#23272f]/80 to-[#181b20]/80 border border-gray-800 rounded-2xl p-3 sm:p-4 md:p-5 mb-6 sm:mb-8 flex items-center gap-3 sm:gap-4 shadow-lg">
          <span className="bg-red-600 p-2 sm:p-3 rounded-full shadow-lg flex-shrink-0">
            <FaUserCheck className="text-white text-lg" />
          </span>
          <div className="min-w-0">
            <h1 className="text-base sm:text-lg font-bold tracking-wide text-white truncate">
              Attendance Management
            </h1>
            <p className="text-sm sm:text-base text-gray-400 truncate">
              View and manage attendance records
            </p>
          </div>
        </div>

        {/* ====== KEEPING YOUR ORIGINAL NAV BUTTONS EXACTLY ====== */}
        <div className="mb-4 sm:mb-6">
          <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 sm:gap-3">
            <Link
              to="/members"
              className="flex-1 min-w-[120px] sm:flex-none flex items-center justify-center gap-1 sm:gap-2 bg-blue-900/50 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm md:text-base font-medium sm:font-semibold text-white border border-blue-700 shadow hover:scale-105 transition focus:outline-none focus:ring-1 sm:focus:ring-2 focus:ring-red-400 group"
            >
              <span className="text-red-400 text-sm sm:text-base">
                <FontAwesomeIcon icon={faUsers} />
              </span>
              <span className="truncate">Members</span>
            </Link>
            <Link
              to="/events"
              className="flex-1 min-w-[120px] sm:flex-none flex items-center justify-center gap-1 sm:gap-2 bg-[#23272f] px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm md:text-base font-medium sm:font-semibold text-white border border-gray-700 shadow hover:scale-105 transition focus:outline-none focus:ring-1 sm:focus:ring-2 focus:ring-orange-400 group"
            >
              <span className="text-orange-400 text-sm sm:text-base">
                <FontAwesomeIcon icon={faChartPie} />
              </span>
              <span className="truncate">Events</span>
            </Link>
            <Link
              to="/scan-qr-code"
              className="flex-1 min-w-[120px] sm:flex-none flex items-center justify-center gap-1 sm:gap-2 bg-[#23272f] px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm md:text-base font-medium sm:font-semibold text-white border border-gray-700 shadow hover:scale-105 transition focus:outline-none focus:ring-1 sm:focus:ring-2 focus:ring-blue-400 group"
            >
              <span className="text-blue-400 text-sm sm:text-base">
                <FontAwesomeIcon icon={faChartBar} />
              </span>
              <span className="truncate">Scan QR</span>
            </Link>
            <Link
              to="/attendance"
              className="flex-1 min-w-[120px] sm:flex-none flex items-center justify-center gap-1 sm:gap-2 bg-green-600 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm md:text-base font-medium sm:font-semibold text-white border border-green-700 shadow hover:scale-105 transition focus:outline-none focus:ring-1 sm:focus:ring-2 focus:ring-green-400 group"
            >
              <span className="text-white text-sm sm:text-base">
                <FontAwesomeIcon icon={faListAlt} />
              </span>
              <span className="truncate">Attendance</span>
            </Link>
          </div>
        </div>

        <div className="mt-8">
          <button
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            onClick={openAddModal}
          >
            <FontAwesomeIcon icon={faPlus} />
            <span>Add New Event</span>
          </button>

          {/* ====== GLASSY BLUE TILES (only this section changed) ====== */}
          <div className="rounded-xl p-6 shadow-lg border border-blue-400/10 bg-gradient-to-br from-blue-900/20 to-indigo-900/20 backdrop-blur-md">
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto">
                  <FontAwesomeIcon icon={faSpinner} className="text-blue-500" spin />
                </div>
                <p className="mt-2 text-blue-200/80">Loading events...</p>
              </div>
            ) : error ? (
              <div className="text-center py-8 text-red-400">
                <p>{error}</p>
                <button
                  onClick={fetchEvents}
                  className="mt-2 px-4 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                >
                  Retry
                </button>
              </div>
            ) : events.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {events.map((event) => {
                  const allDay = !event.start_time && !event.end_time;
                  return (
                    <div
                      key={event.id}
                      className="relative overflow-hidden rounded-xl border border-blue-300/20 bg-gradient-to-br from-sky-500/10 via-blue-500/10 to-indigo-500/10 backdrop-blur-md shadow-[0_10px_40px_-20px_rgba(0,0,0,0.6)] hover:shadow-[0_20px_60px_-25px_rgba(0,0,0,0.65)] transition-shadow"
                    >
                      {/* subtle light gleam */}
                      <div className="pointer-events-none absolute -top-16 -right-16 h-40 w-40 rounded-full bg-gradient-to-br from-white/20 to-transparent blur-2xl" />
                      <div className="p-4">
                        <div className="flex justify-between items-start gap-3">
                          <div className="flex-1 min-w-0">
                            <h3 className="text-lg font-semibold text-white/95 truncate">
                              {event.event_name}
                            </h3>
                            <div className="mt-1 flex items-center flex-wrap text-sm text-blue-100/90 gap-x-2">
                              <FaCalendarAlt className="opacity-80" />
                              <span>{formatDate(event.event_date)}</span>
                              {allDay ? (
                                <span className="px-2 py-0.5 rounded-full text-xs bg-blue-300/15 border border-blue-300/20 text-blue-100/90">
                                  All-day
                                </span>
                              ) : (
                                <>
                                  {event.start_time && (
                                    <span>• {formatTimeForInput(event.start_time)}</span>
                                  )}
                                  {event.end_time && (
                                    <span>– {formatTimeForInput(event.end_time)}</span>
                                  )}
                                </>
                              )}
                            </div>
                            {event.location && (
                              <p className="text-sm text-blue-100/80 mt-2 break-words">
                                📍 {event.location}
                              </p>
                            )}
                            {event.description && (
                              <p className="text-sm text-blue-100/80 mt-2 line-clamp-2 break-words">
                                {event.description}
                              </p>
                            )}
                          </div>

                          <div className="flex gap-2 shrink-0">
                            <button
                              className="p-2 rounded-lg bg-white/10 hover:bg-white/20 border border-white/20"
                              onClick={() => openEditModal(event)}
                              title="Edit Event"
                            >
                              <FontAwesomeIcon icon={faEdit} className="text-white/90" />
                            </button>
                            <button
                              className="p-2 rounded-lg bg-red-500/15 hover:bg-red-500/25 border border-red-400/30"
                              onClick={() => confirmDelete(event.id)}
                              title="Delete Event"
                            >
                              <FontAwesomeIcon icon={faTrash} className="text-red-300" />
                            </button>
                          </div>
                        </div>

                        <div className="mt-3 pt-3 border-t border-blue-300/20 flex items-center justify-between">
                          <div className="flex items-center text-sm text-blue-100/80 gap-1.5">
                            <FaUserCheck />
                            <span>0 attendees</span>
                          </div>
                          <Link
                            to={`/attendance?event_id=${event.id}`}
                            className="text-sm text-sky-300 hover:underline"
                          >
                            View Attendance
                          </Link>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 border border-blue-300/20 bg-blue-400/10 backdrop-blur">
                  <FaCalendarAlt className="text-2xl text-blue-100/90" />
                </div>
                <h3 className="text-lg font-medium text-white mb-1">No events yet</h3>
                <p className="text-blue-100/80 mb-4">Get started by creating your first event</p>
                <button
                  onClick={openAddModal}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg inline-flex items-center gap-2 transition-colors"
                >
                  <FontAwesomeIcon icon={faPlus} />
                  <span>Create Event</span>
                </button>
              </div>
            )}
          </div>

          {/* Add/Edit Event Modal (unchanged style) */}
          {isModalOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
              <div className="bg-gray-800 rounded-xl w-full max-w-md relative">
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="absolute top-4 right-4 text-gray-400 hover:text-white"
                >
                  <FaTimes className="text-xl" />
                </button>

                <div className="p-6">
                  <h3 className="text-xl font-bold text-white mb-6">
                    {currentEvent ? 'Edit Event' : 'Create New Event'}
                  </h3>

                  <form onSubmit={handleSubmit}>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                          Event Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="event_name"
                          value={formData.event_name}
                          onChange={handleInputChange}
                          className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          required
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-1">
                            Date <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="date"
                            name="event_date"
                            value={formData.event_date}
                            onChange={handleInputChange}
                            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-1">
                            Start Time
                          </label>
                          <input
                            type="time"
                            name="start_time"
                            value={formData.start_time}
                            onChange={handleInputChange}
                            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-1">
                            End Time
                          </label>
                          <input
                            type="time"
                            name="end_time"
                            value={formData.end_time}
                            onChange={handleInputChange}
                            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>

                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-300 mb-1">
                            Location
                          </label>
                          <input
                            type="text"
                            name="location"
                            value={formData.location}
                            onChange={handleInputChange}
                            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="e.g., Main Hall, Room 101"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                          Description
                        </label>
                        <textarea
                          name="description"
                          value={formData.description}
                          onChange={handleInputChange}
                          rows="3"
                          className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Add event details..."
                        ></textarea>
                      </div>

                      <div className="flex justify-end gap-3 pt-4">
                        <button
                          type="button"
                          onClick={() => setIsModalOpen(false)}
                          className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800"
                        >
                          {currentEvent ? 'Update Event' : 'Create Event'}
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}

          {/* Delete Confirmation Modal (unchanged style) */}
          {showDeleteConfirm && (
            <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
              <div className="bg-gray-800 rounded-xl w-full max-w-md p-6">
                <div className="text-center">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                    <FaTimesCircle className="h-6 w-6 text-red-600" />
                  </div>
                  <h3 className="text-lg font-medium text-white mb-2">Delete Event</h3>
                  <p className="text-gray-300 mb-6">
                    Are you sure you want to delete this event? This action cannot be undone.
                  </p>
                  <div className="flex justify-center space-x-4">
                    <button
                      type="button"
                      onClick={() => handleDelete(false)}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-300 hover:text-white hover:bg-gray-700"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(true)}
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default EventsPage;
